// Target achievement — every mapped user's kg target vs what they achieved.
// (A person's own target stays on the "My targets" page.)
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PageHeader from "@/components/PageHeader";

const ROLES = ["SE", "ASM", "ZSM", "RSM", "SALES_HEAD"];
const kg = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const monthOptions = () => {
  const now = new Date(Date.now() + 5.5 * 3600e3);
  return Array.from({ length: 15 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1 - i, 1));
    return { v: d.toISOString().slice(0, 7), label: `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` };
  });
};
const thisMonth = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 7);

export default function TargetAchievementReport() {
  const reportsBackTo = useReportsBackLink();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("month");
  const [period, setPeriod] = useState(thisMonth());
  const [role, setRole] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [subZoneId, setSubZoneId] = useState("");
  const [search, setSearch] = useState("");

  const params = { period_type: periodType, period_start: period, ...(role ? { role_code: role } : {}),
    ...(zoneId ? { zone_id: zoneId } : {}), ...(subZoneId ? { sub_zone_id: subZoneId } : {}) };

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/target-incentive", { params: { ...params, page_size: 1000 } });
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [periodType, period, role, zoneId, subZoneId]); // eslint-disable-line

  async function download(format) {
    try {
      const res = await api.get("/reports/target-incentive", { params: { ...params, format }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `target-achievement-${period}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => !q || (r.name || "").toLowerCase().includes(q));
  }, [rows, search]);
  const withTarget = shown.filter(r => r.t1_kg > 0);
  const showT2 = shown.some(r => r.t2_kg > 0);
  const showT3 = shown.some(r => r.t3_kg > 0);
  const reached = withTarget.filter(r => r.achieved_kg >= r.t1_kg).length;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="ta-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: "Target achievement" }]}
        title="Target achievement report"
        subtitle="Target and achievement (kg) of each person mapped to you."
        favorite={{ key: "reports.targets", type: "screen", label: "Target achievement report", route: "/reports/target-achievement" }}
        testid="targetachievementreport-page-header"
      />

      <CommonFilterBar
        value={{ zone_id: zoneId, sub_zone_id: subZoneId }}
        onChange={(f) => { setZoneId(f.zone_id || ""); setSubZoneId(f.sub_zone_id || ""); }}
        showZone showSubZone
      />

      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <Label>Period</Label>
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{periodType === "quarter" ? "First month of quarter" : "Month"}</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44 h-9" data-testid="ta-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions().map(m => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Role</Label>
          <Select value={role || "__all__"} onValueChange={(v) => setRole(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All roles</SelectItem>
              {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <Label>Search name</Label>
          <Input className="h-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a name" />
        </div>
        <Button variant="ghost" size="sm" onClick={reload}><RefreshCw className="w-4 h-4" /></Button>
        <Button variant="outline" onClick={() => download("xlsx")} data-testid="ta-xlsx"><FileSpreadsheet className="w-3 h-3 mr-1" /> Excel</Button>
        <Button onClick={() => download("pdf")} data-testid="ta-pdf"><FileText className="w-3 h-3 mr-1" /> PDF</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="bg-white border rounded-lg p-3 min-w-[110px] flex-1">
          <div className="text-xs text-neutral-500">People</div>
          <div className="text-2xl font-bold">{shown.length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 min-w-[110px] flex-1">
          <div className="text-xs text-emerald-700">Reached target</div>
          <div className="text-2xl font-bold text-emerald-800">{reached}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 min-w-[110px] flex-1">
          <div className="text-xs text-rose-700">Not reached</div>
          <div className="text-2xl font-bold text-rose-800">{withTarget.length - reached}</div>
        </div>
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : shown.length === 0
         ? <div className="text-neutral-500 border border-dashed rounded-lg p-10 text-center">No people to show.</div>
         : (
        <div className="bg-white border rounded-lg overflow-auto max-h-[65vh]">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b sticky top-0">
              <tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Sub-zone</th>
                <th className="text-right p-2">Target{showT2 || showT3 ? " 1" : ""}</th>
                {showT2 && <th className="text-right p-2">Target 2</th>}
                {showT3 && <th className="text-right p-2">Target 3</th>}
                <th className="text-right p-2">Secondary (kg)</th>
                <th className="text-right p-2">Primary (kg)</th>
                <th className="text-right p-2">%</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(r => {
                const pct = r.t1_kg ? Math.round((r.achieved_kg / r.t1_kg) * 100) : null;
                return (
                  <tr key={r.user_id} className="border-b hover:bg-neutral-50" data-testid={`ta-row-${r.user_id}`}>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2">{r.role_code}</td>
                    <td className="p-2">{r.sub_zone}</td>
                    <td className="p-2 text-right">{kg(r.t1_kg)}</td>
                    {showT2 && <td className="p-2 text-right">{kg(r.t2_kg)}</td>}
                    {showT3 && <td className="p-2 text-right">{kg(r.t3_kg)}</td>}
                    <td className="p-2 text-right">{kg(r.achieved_kg)}</td>
                    <td className="p-2 text-right">{kg(r.primary_kg)}</td>
                    <td className="p-2 text-right font-semibold">{pct === null ? "—" : `${pct}%`}</td>
                    <td className="p-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${r.earned_level ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-neutral-50 border-neutral-200 text-neutral-600"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
       )}
    </div>
  );
}
